import React from 'react';
import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
    isSortOrder,
    SortOrder,
} from '../../graphql/pimcore/modules/resourceLibrary/types/sorting';
import { useResourceLibraryItems } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceLibraryItemsProvider';
import { ResourceLibraryItem } from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { useCommonContent } from '../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';
import { formatIcuString } from '../../utils/Locale';
import { useLocale } from '../../contexts/LocaleProvider';

interface ResultHeaderProps {
    isDataSheet: boolean;
    items: ResourceLibraryItem[];
    sort: SortOrder;
    setSort: (sort: SortOrder) => void;
    onReset?: () => void;
    view: string;
    setView: React.Dispatch<React.SetStateAction<string>>;
}

const ResultHeader: React.FC<ResultHeaderProps> = ({
    isDataSheet,
    items,
    sort,
    setSort,
    onReset,
    view,
    setView,
}) => {
    const { refetch, resetFilters } = useResourceLibraryItems();
    const { contentTree: cmsCommonContent } = useCommonContent();
    const { locale } = useLocale();
    const cmsContent = cmsCommonContent.ResourceLibrary;
    const { isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    const isSmallScreen = isScreenMd || isScreenSm || isScreenXs;
    const handleSortChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        if (isSortOrder(value)) {
            setSort(value);
        } else {
            console.error(`Invalid sort order: ${value}`);
        }
    };

    const handleResetButtonClick = () => {
        refetch();
        resetFilters();
        onReset?.();
    };

    return (
        <Box
            display="flex"
            justifyContent={isDataSheet ? 'flex-end' : 'space-between'}
            alignItems="center"
            width="100%"
            p={2}
        >
            <Box display="flex" alignItems="center">
                <Typography variant="body1">
                    {formatIcuString(
                        cmsContent.numberOfResults.content,
                        { count: items.length },
                        locale
                    )}
                </Typography>
                {!isSmallScreen && (
                    <>
                        <IconButton onClick={handleResetButtonClick}>
                            <RefreshIcon />
                            <Typography variant="body1" sx={{ color: '#000' }}>
                                {cmsContent.reset.content}
                            </Typography>
                        </IconButton>
                    </>
                )}
            </Box>
            <Box display="flex" alignItems="center">
                <IconButton
                    onClick={() => setView('module')}
                    sx={{ color: view === 'module' ? '#6c6c6c' : '#d3d3d3' }}
                >
                    <ViewModuleIcon />
                </IconButton>
                <IconButton
                    onClick={() => setView('list')}
                    sx={{ color: view === 'list' ? '#6c6c6c' : '#d3d3d3' }}
                >
                    <ViewListIcon />
                </IconButton>
                <FormControl variant="outlined" size="small" sx={{ ml: 2 }}>
                    <InputLabel>{cmsContent.sortOptionLabel.content}</InputLabel>
                    <Select value={sort} onChange={handleSortChange} label="Sort">
                        <MenuItem value="latest">{cmsContent.sortByLatestFirst.content}</MenuItem>
                        <MenuItem value="oldest">{cmsContent.sortByOldestFirst.content}</MenuItem>
                        <MenuItem value="relevance">
                            {cmsContent.sortByMostRelevantFirst.content}
                        </MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
};

export default ResultHeader;
